import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@framework/ui/atoms/Button';
import { Input } from '@framework/ui/atoms/Input';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { WorkspaceOperation } from '@workspace/domain/WorkspaceOperation';
import type { WorkspaceInternalPublicMemberRoleJSON } from 'balus-common';
import { useTranslation } from 'react-i18next';

type InternalPublicSetting = WorkspaceInternalPublicMemberRoleJSON | null;

type Props = {
  groupId: string;
  userId: string;
  isOpen: boolean;
  onClose(): void;
};

export const CreateWorkspaceModal: React.FC<Props> = ({ groupId, userId, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const internalPublicSettingOptions: Record<WorkspaceInternalPublicMemberRoleJSON, string> = useMemo(
    () => ({
      editor: t('group.createWorkspace.roleEditor'),
      viewer: t('group.createWorkspace.roleViewer'),
    }),
    [t]
  );
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isCreatable, setIsCreatable] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [name, setName] = useState<string>('');

  // グループ内公開設定
  const [internalPublicSetting, setInternalPublicSetting] = useState<InternalPublicSetting>(null);
  const handlePrivateWorkspace = useCallback(() => setInternalPublicSetting(null), []);
  const handleInternalPublicWorkspace = useCallback(() => setInternalPublicSetting('editor'), []);
  const handleChangeInternalSetting = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      if (Object.prototype.hasOwnProperty.call(internalPublicSettingOptions, value)) {
        setInternalPublicSetting(value as InternalPublicSetting);
      }
    },
    [internalPublicSettingOptions]
  );

  const handleClose = useCallback(() => {
    setIsProcessing(false);
    setName('');
    setInternalPublicSetting(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setIsProcessing(true);

    try {
      // ワークスペースを作成してからグループ内公開設定を更新する
      const workspaceId = await WorkspaceOperation.create(name, groupId, userId);
      await WorkspaceOperation.updateInternalPublicSetting(workspaceId, internalPublicSetting);
      handleClose();
    } catch (e) {
      setErrorMessage(t('common.message.errorOccurred'));
      console.error(e);
    } finally {
      setIsProcessing(false);
    }
  }, [name, groupId, userId, internalPublicSetting, handleClose, t]);

  useEffect(() => {
    setIsCreatable(name.length > 0);
  }, [name]);

  const handleChangeName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('group.createWorkspace.modalTitle')}
      submitButton={
        <Button color={'primary'} onClick={handleSubmit} disabled={!isCreatable} loading={isProcessing}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button color={'secondary'} onClick={handleClose} disabled={isProcessing}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div className="p-2">
        <Input
          type="text"
          placeholder={t('group.createWorkspace.namePlaceholder')}
          value={name}
          onChange={handleChangeName}
          disabled={isProcessing}
          autoFocus={true}
        />
        {errorMessage && <div className="text-sm text-red-700">{errorMessage}</div>}
      </div>

      <div className="p-2">
        <label className="block cursor-pointer">
          <input
            type="radio"
            name="internal-setting"
            className="form-radio mr-1 size-6 border-brand"
            checked={internalPublicSetting === null}
            onChange={handlePrivateWorkspace}
            disabled={isProcessing}
          />
          {t('group.createWorkspace.privateLabel')}
        </label>
        <span className="ml-8 py-1 text-sm">{t('common.label.privateWorkspaceDescription')}</span>
      </div>

      <div className="p-2">
        <label className="block cursor-pointer">
          <input
            type="radio"
            name="internal-setting"
            className="form-radio mr-1 size-6 border-brand"
            checked={internalPublicSetting !== null}
            onChange={handleInternalPublicWorkspace}
            disabled={isProcessing}
          />
          {t('common.label.internalPublicWorkspace')}
        </label>
        <span className="ml-8 py-1 text-sm">{t('common.label.internalPublicWorkspaceDescription')}</span>

        <div className="ml-8">
          <select
            className="form-select block w-64 py-1"
            defaultValue={internalPublicSetting || ''} // defaultValue に null を指定できないため空文字列に fallback する
            onChange={handleChangeInternalSetting}
            disabled={isProcessing || internalPublicSetting == null}
          >
            {Object.entries(internalPublicSettingOptions).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </GeneralModal>
  );
};
